import {
  Autocomplete,
  AutocompleteInputChangeReason,
  TextField,
  CircularProgress,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  TextFieldProps,
  TextFieldVariants,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {useDebounceCallback} from "hooks/useDebounceCallback";
import useMasks from "hooks/useMasks";
import pxToRem from "hooks/usePxToRem";
import React, {useEffect, useRef} from "react";

type Props = Omit<AutocompleteProps<any, any, any, any, any>, "renderInput"> & {
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  getListByText?: (text: string) => Promise<void>;
  getNextPage?: () => Promise<void> | void;
  textFieldProps?: TextFieldProps;
  permanentPlaceholderSize?: number;
  InnerInput?: <Variant extends TextFieldVariants>(
    props: {
      variant?: Variant;
    } & Omit<TextFieldProps, "variant">,
  ) => JSX.Element;
  name?: string;
  tooltipText?: string;
  required?: boolean;
  optionSecondaryContent?: boolean;
  textFieldVariant?: "outlined" | "standard" | "filled";
  label?: string;
  error?: boolean;
  debounceDelay?: number;
};

// eslint-disable-next-line react/display-name
const CustomAutocomplete = React.forwardRef((props: Props, ref) => {
  const {
    getListByText = () => void 0,
    getNextPage = () => void 0,
    textFieldProps,
    permanentPlaceholderSize = 0.1,
    tooltipText,
    required = false,
    optionSecondaryContent,
    textFieldVariant = "outlined",
    debounceDelay = 300,
    onInputChange = (e, newInputValue) => {
      if (e?.type === "change") {
        getListByText(newInputValue);
      }
    },
    label,
    error = false,
    ...rest
  } = props;

  const {cnpjMask} = useMasks();
  const theme = useTheme();
  const onInputChangeRef = useRef(onInputChange);

  useEffect(() => {
    onInputChangeRef.current = onInputChange;
  }, [onInputChange]);

  const percentPermanentPlaceholderSize = `${permanentPlaceholderSize * 8.33}%`;

  const onBottomScroll = async () => {
    await getNextPage();
    return false;
  };

  const optionSecondaryText = (option) => {
    if (typeof option?.additional === "string") {
      return option?.additional;
    }
    if (option?.additional?.cnpj) {
      return `CNPJ: ${cnpjMask(option?.additional?.cnpj)}`;
    } else if (option?.additional?.vatNumber) {
      return `VAT Number: ${option?.additional?.vatNumber}`;
    }
    return "-";
  };

  const debouncedOnInputChange = useDebounceCallback(
    (
      event: React.SyntheticEvent<Element, Event>,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      onInputChangeRef.current?.(event, value, reason);
    },
    debounceDelay,
  );

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason !== "input") {
      debouncedOnInputChange.cancel();
      return;
    }

    if (debounceDelay <= 0) {
      onInputChangeRef.current?.(event, value, reason);
      return;
    }

    debouncedOnInputChange(event, value, reason);
  };

  return (
    <Tooltip title={tooltipText} placement="top" arrow>
      <Autocomplete
        ref={ref}
        sx={{...props.sx}}
        isOptionEqualToValue={(option, value) => option.id == value.id}
        freeSolo={false}
        loading={rest.loading}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const listboxNode = event.currentTarget;

            if (
              listboxNode.scrollTop + listboxNode.clientHeight >
              listboxNode.scrollHeight - 1
            ) {
              onBottomScroll();
            }
          },
        }}
        noOptionsText="Sem resultados"
        autoComplete
        onInputChange={handleInputChange}
        {...rest}
        onOpen={rest.options?.length <= 1 ? rest.onOpen : undefined}
        renderOption={(props, option, state) => {
          return (
            <li
              {...props}
              key={`${option?.id}.${state?.index}`}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography>{option.label}</Typography>
              {optionSecondaryContent && (
                <Typography style={{fontSize: pxToRem(12)}}>
                  {optionSecondaryText(option)}
                </Typography>
              )}
            </li>
          );
        }}
        renderInput={(params) =>
          //Params passados para o componente input que vem por props estão bem genéricos,
          // avaliar conforme o uso se vai ser necessário passar mais coisas
          rest.InnerInput ? (
            <rest.InnerInput {...params} {...textFieldProps} />
          ) : (
            <TextField
              {...params}
              error={error}
              required={required}
              label={label}
              size="small"
              placeholder={rest.placeholder}
              variant={textFieldVariant}
              slotProps={{
                input: {
                  style: {
                    fontSize: rest?.sx?.["fontSize"],
                    height: rest?.sx?.["height"] ?? "auto",
                  },
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {rest.loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                },
                inputLabel: {
                  style: {
                    fontSize: rest?.sx?.["fontSize"] ?? "default",
                    color: props.disabled
                      ? theme.palette.grey[300]
                      : theme.palette.grey[500],
                  },
                },
              }}
              {...textFieldProps}
              sx={{
                fontSize: pxToRem(10),
                ".MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary.MuiInputBase-fullWidth.MuiInputBase-formControl":
                  {
                    paddingLeft: percentPermanentPlaceholderSize,
                  },
              }}
            />
          )
        }
      />
    </Tooltip>
  );
});

export default CustomAutocomplete;
